import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Search, Download, Upload, Filter, QrCode, Image as ImageIcon, CheckCircle2, AlertCircle, Copy, ArrowUp, ArrowDown, ChevronsUpDown, ShieldCheck, X } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import InciAnalysisPanel from "@/components/InciAnalysisPanel";
import { ProductStatus } from "@/components/ProductStatus";
import { useAuth } from "@/lib/auth";
import { getActiveToken } from "@/lib/sessionToken";

const ALL_COLS = [
  { key: "status", label: "Stato" },
  { key: "photo", label: "Foto" },
  { key: "code", label: "Codice" },
  { key: "name", label: "Nome" },
  { key: "brand", label: "Brand" },
  { key: "category", label: "Categoria" },
  { key: "subcategory", label: "Sottocategoria" },
  { key: "quantity", label: "Quantità" },
  { key: "price", label: "Prezzo" },
  { key: "qr", label: "QR" },
  { key: "updated", label: "Ultima modifica" },
  { key: "actions", label: "Azioni" },
];

export default function Dashboard() {
  const nav = useNavigate();
  const { user, canWrite, subscriptionDisabled } = useAuth();
  const isAdmin = canWrite;
  const PAGE_SIZE = 50;
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);        // initial / reset load
  const [loadingMore, setLoadingMore] = useState(false); // appending next page
  const loadingRef = useRef(false);
  const scrollRef = useRef(null);
  const sentinelRef = useRef(null);
  const [brands, setBrands] = useState([]);
  const [cats, setCats] = useState([]);
  const [qrModal, setQrModal] = useState(null);
  const [dupModal, setDupModal] = useState(null);
  const [inciModal, setInciModal] = useState(null);
  const [priceBounds, setPriceBounds] = useState({ min: 0, max: 500 });
  const [filters, setFilters] = useState({ q: "", brand_id: "", category_id: "",
    subcategory_id: "", code: "", barcode: "", inci: "", inci_min_percent: "",
    cas: "", price_min: "", price_max: "" });
  const [cols, setCols] = useState(() => {
    const saved = localStorage.getItem("cosm_cols");
    if (!saved) return ALL_COLS.map(c => c.key);
    const parsed = JSON.parse(saved);
    // Migration: ensure newly added columns are visible by default.
    if (!parsed.includes("status")) parsed.unshift("status");
    if (!parsed.includes("actions")) parsed.push("actions");
    return parsed;
  });
  const [showFilters, setShowFilters] = useState(false);
  const [usage, setUsage] = useState(null);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportSort, setExportSort] = useState("code");
  const [exporting, setExporting] = useState(false);
  const [showCols, setShowCols] = useState(false);
  const [onlyIncomplete, setOnlyIncomplete] = useState(false);
  const [incompleteCount, setIncompleteCount] = useState(null);
  const [sortField, setSortField] = useState(() => localStorage.getItem("cosm_sort_field") || "");
  const [sortDir, setSortDir] = useState(() => localStorage.getItem("cosm_sort_dir") || "asc");
  useEffect(() => {
    localStorage.setItem("cosm_sort_field", sortField);
    localStorage.setItem("cosm_sort_dir", sortDir);
  }, [sortField, sortDir]);
  const toggleSort = (field) => {
    if (sortField === field) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortField(field); setSortDir("asc"); }
  };

  const buildParams = (nextSkip, f = filters) => {
    const params = Object.fromEntries(
      Object.entries(f).filter(([_, v]) => v !== "" && v !== null));
    params.limit = PAGE_SIZE;
    params.skip = nextSkip;
    if (sortField) { params.sort_field = sortField; params.sort_dir = sortDir; }
    if (onlyIncomplete) params.only_incomplete = true;
    return params;
  };

  // Core loader. replace=true → fresh list (resets scroll); false → append page.
  const loadProducts = async (nextSkip, replace, f = filters) => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    if (replace) setLoading(true); else setLoadingMore(true);
    try {
      const r = await api.get("/products", { params: buildParams(nextSkip, f) });
      setTotal(r.data.total);
      if (onlyIncomplete) setIncompleteCount(r.data.total);
      setItems(prev => replace ? r.data.items : [...prev, ...r.data.items]);
      if (replace && scrollRef.current) scrollRef.current.scrollTop = 0;
    } catch (e) { toast.error("Errore caricamento prodotti"); }
    finally { loadingRef.current = false; setLoading(false); setLoadingMore(false); }
  };
  // Kept under the name `fetchAll` so existing callers (search, filters,
  // QR regen) simply reset the list to page 1.
  const fetchAll = (f = filters) => loadProducts(0, true, f);

  // Count of incomplete products for the "Da completare" badge (independent
  // of the current filter). Refreshed on mount and whenever the list reloads.
  const fetchIncompleteCount = async () => {
    try {
      const r = await api.get("/products", { params: { only_incomplete: true, limit: 1, light: true } });
      setIncompleteCount(r.data.total);
    } catch { /* silent */ }
  };
  useEffect(() => { fetchIncompleteCount(); /* eslint-disable-next-line */ }, []);

  // Reference data (brands / categories / price bounds) — once.
  useEffect(() => {
    api.get("/brands").then(r => setBrands(r.data));
    api.get("/categories").then(r => setCats(r.data));
    api.get("/me/usage").then(r => setUsage(r.data)).catch(()=>{});
    api.get("/products/price-range").then(r => {
      const min = Math.floor(r.data.min || 0);
      const max = Math.ceil(r.data.max || 500);
      // Guard against degenerate bounds (single product, all same price).
      setPriceBounds({ min, max: max > min ? max : min + 1 });
    }).catch(() => {});
  }, []);

  // Server-side sort / "da completare" toggle → reset list. Also fires on
  // mount, which performs the initial products load.
  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, [sortField, sortDir, onlyIncomplete]);

  // Live text search: reset (debounced) as the user types. Skips first run.
  const skipFirstSearch = useRef(true);
  useEffect(() => {
    if (skipFirstSearch.current) { skipFirstSearch.current = false; return; }
    const t = setTimeout(() => { fetchAll(); }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.q]);

  // Infinite scroll: (re)create an observer on the bottom sentinel whenever the
  // list grows. Recreating forces a fresh intersection check, so short lists
  // that don't fill the viewport keep auto-loading until the page is full.
  useEffect(() => {
    const root = scrollRef.current;
    const target = sentinelRef.current;
    if (!root || !target || loading || items.length >= total) return;
    const io = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !loadingRef.current) {
        loadProducts(items.length, false);
      }
    }, { root, rootMargin: "300px" });
    io.observe(target);
    return () => io.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.length, total, loading]);

  const toggleCol = (k) => {
    const next = cols.includes(k) ? cols.filter(c => c !== k) : [...cols, k];
    setCols(next);
    localStorage.setItem("cosm_cols", JSON.stringify(next));
  };

  const exportXlsx = async (sort = "code") => {
    const params = Object.fromEntries(Object.entries(filters).filter(([_, v]) => v !== "" && v !== null));
    if (onlyIncomplete) params.only_incomplete = true;
    params.sort = sort;
    const token = getActiveToken();
    const url = new URL(`${process.env.REACT_APP_BACKEND_URL}/api/products/export/xlsx`);
    Object.entries(params).forEach(([k,v]) => url.searchParams.set(k, v));
    try {
      const res = await fetch(url.toString(), { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("export failed");
      const blob = await res.blob();
      const d = new Date();
      const pad = (n) => String(n).padStart(2, "0");
      const dateStr = `${pad(d.getDate())}_${pad(d.getMonth()+1)}_${String(d.getFullYear()).slice(-2)}`;
      const sanitize = (s) => (s || "").normalize("NFD").replace(/[\u0300-\u036f]/g,"")
        .replace(/[^a-zA-Z0-9]+/g,"_").replace(/^_+|_+$/g,"");
      let base = "prodotti_completo";
      if (filters.brand_id) {
        const bn = sanitize(brandMap[filters.brand_id]?.name);
        base = bn ? `${bn}_prodotti` : "prodotti_completo";
      }
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${base}_${dateStr}.xlsx`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch { toast.error("Errore durante l'esportazione Excel"); }
  };

  const brandMap = useMemo(() => Object.fromEntries(brands.map(b => [b.id, b])), [brands]);
  const catMap = useMemo(() => Object.fromEntries(cats.map(c => [c.id, c])), [cats]);

  const subcats = cats.filter(c => c.parent_id);
  const topCats = cats.filter(c => !c.parent_id);
  const hasMore = items.length < total;

  const removeFilter = (key) => {
    const next = { ...filters };
    if (key === "price") { next.price_min = ""; next.price_max = ""; }
    else if (key === "category_id") { next.category_id = ""; next.subcategory_id = ""; }
    else next[key] = "";
    setFilters(next);
    fetchAll(next);
  };
  const EMPTY_FILTERS = { q:"", brand_id:"", category_id:"", subcategory_id:"", code:"", barcode:"", inci:"", inci_min_percent:"", cas:"", price_min:"", price_max:"" };
  const clearAllFilters = () => {
    setFilters(EMPTY_FILTERS);
    fetchAll(EMPTY_FILTERS);
  };
  const filterChips = [];
  if (filters.q) filterChips.push({ key:"q", label:`Ricerca: "${filters.q}"` });
  if (filters.brand_id) filterChips.push({ key:"brand_id", label:`Brand: ${brandMap[filters.brand_id]?.name || filters.brand_id}` });
  if (filters.category_id) filterChips.push({ key:"category_id", label:`Categoria: ${catMap[filters.category_id]?.name || filters.category_id}` });
  if (filters.subcategory_id) filterChips.push({ key:"subcategory_id", label:`Sotto-categoria: ${catMap[filters.subcategory_id]?.name || filters.subcategory_id}` });
  if (filters.code) filterChips.push({ key:"code", label:`Codice: ${filters.code}` });
  if (filters.barcode) filterChips.push({ key:"barcode", label:`Barcode: ${filters.barcode}` });
  if (filters.inci) filterChips.push({ key:"inci", label:`INCI: ${filters.inci}` });
  if (filters.cas) filterChips.push({ key:"cas", label:`CAS: ${filters.cas}` });
  if (filters.inci_min_percent) filterChips.push({ key:"inci_min_percent", label:`% min: ${filters.inci_min_percent}` });
  if (filters.price_min !== "" || filters.price_max !== "") filterChips.push({ key:"price", label:`Prezzo: ${filters.price_min!==""?filters.price_min:priceBounds.min}–${filters.price_max!==""?filters.price_max:priceBounds.max} €` });

  const atProductLimit = usage && usage.max_products > 0 && usage.products_count >= usage.max_products;
  const handleNewProduct = () => {
    if (atProductLimit) {
      toast.error(`Hai raggiunto il limite di ${usage.max_products} prodotti previsto dal tuo abbonamento. Contatta l'amministratore per aumentarlo.`);
      return;
    }
    nav("/products/new");
  };

  return (
    <div className="products-ux h-full flex flex-col p-4 pt-16 md:p-8 lg:p-12" data-testid="dashboard">
      <div className="flex flex-col sm:flex-row justify-between sm:items-end gap-4 mb-6 md:mb-8">
        <div>
          <div className="text-[10px] tracking-luxury text-[#707070] uppercase mb-1">Catalogo</div>
          <h1 className="font-serif text-3xl md:text-4xl">Prodotti</h1>
          <div className="text-sm text-[#707070] mt-1">
            {onlyIncomplete ? `${total} da completare` : `${total} prodotti totali`}
          </div>
          {usage && (
            <div className="text-xs mt-1" data-testid="product-limit-info">
              {usage.max_products > 0 ? (
                <span className={atProductLimit ? "text-[#B24040] font-medium" : "text-[#707070]"}>
                  Prodotti: {usage.products_count}/{usage.max_products} · {Math.max(0, usage.max_products - usage.products_count)} rimanenti
                </span>
              ) : (
                <span className="text-[#707070]">Prodotti: {usage.products_count} · illimitato</span>
              )}
            </div>
          )}
        </div>
        <div className="flex gap-2 md:gap-3 flex-wrap">
          <button onClick={() => setOnlyIncomplete(v=>!v)}
                  aria-pressed={onlyIncomplete}
                  className="btn-ghost flex items-center product-ux-filter-alert"
                  data-testid="filter-incomplete-btn">
            <AlertCircle className="w-3 h-3 mr-2" strokeWidth={1.5} />
            Da completare{incompleteCount != null ? ` (${incompleteCount})` : ""}
          </button>
          <button onClick={() => setShowCols(s=>!s)} className="btn-ghost flex items-center" data-testid="toggle-columns-btn">
            Colonne
          </button>
          <button onClick={() => setShowFilters(s=>!s)}
                  className={`btn-ghost flex items-center ${showFilters ? "bg-[#1A1A1A] text-[#FAF9F6] border-[#1A1A1A]" : (filterChips.length ? "!border-[#B45309] !text-[#B45309]" : "")}`}
                  data-testid="toggle-filters-btn">
            <Filter className="w-3 h-3 mr-2" strokeWidth={1.5} />{showFilters ? "Chiudi filtri" : (filterChips.length ? `Filtri (${filterChips.length})` : "Filtri")}
          </button>
          <button onClick={() => setShowExportModal(true)} className="btn-ghost flex items-center" data-testid="export-xlsx-btn">
            <Download className="w-3 h-3 mr-2" strokeWidth={1.5} />Download Excel
          </button>
          {isAdmin && (
            <button onClick={() => nav("/products/import")} className="btn-ghost flex items-center" data-testid="import-xlsx-btn">
              <Upload className="w-3 h-3 mr-2" strokeWidth={1.5} />Importa da Excel
            </button>
          )}
          {canWrite && <button onClick={handleNewProduct} className={`btn-primary flex items-center ${atProductLimit ? "opacity-60" : ""}`} data-testid="new-product-btn">
            <Plus className="w-3 h-3 mr-2" strokeWidth={2} />Nuovo prodotto
          </button>}
        </div>
      </div>

      {showExportModal && createPortal(
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/40 p-4"
             data-testid="export-modal" onClick={() => !exporting && setShowExportModal(false)}>
          <div className="bg-white w-full max-w-md border border-[#E5E5E0] shadow-xl" onClick={(e)=>e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#F0F0EA]">
              <h3 className="font-serif text-lg">Download Excel</h3>
              <button onClick={() => setShowExportModal(false)} className="text-[#707070] hover:text-[#1A1A1A]" data-testid="export-modal-close">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-6">
              <div className="text-[10px] tracking-luxury uppercase text-[#707070] mb-3">Ordina i prodotti per</div>
              <div className="space-y-2">
                {[
                  { v: "code", label: "Codice prodotto" },
                  { v: "name", label: "Nome prodotto" },
                  { v: "cat_sub", label: "Categoria e sottocategoria" },
                ].map(opt => (
                  <label key={opt.v}
                         className={`flex items-center gap-3 px-4 py-3 border cursor-pointer transition ${exportSort === opt.v ? "border-[#1A1A1A] bg-[#FAFAF7]" : "border-[#E5E5E0] hover:border-[#C5B39B]"}`}
                         data-testid={`export-sort-${opt.v}`}>
                    <input type="radio" name="export-sort" value={opt.v}
                           checked={exportSort === opt.v}
                           onChange={() => setExportSort(opt.v)} />
                    <span className="text-sm text-[#1A1A1A]">{opt.label}</span>
                  </label>
                ))}
              </div>
              <div className="mt-4 text-xs text-[#707070]">
                Verranno esportati <strong>{total}</strong> prodotti (gli stessi mostrati nella lista con i filtri attivi).
              </div>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-[#F0F0EA]">
              <button onClick={() => setShowExportModal(false)} className="btn-ghost text-sm" data-testid="export-modal-cancel">Annulla</button>
              <button onClick={async () => { setExporting(true); await exportXlsx(exportSort); setExporting(false); setShowExportModal(false); }}
                      disabled={exporting}
                      className="btn-primary text-sm inline-flex items-center gap-2" data-testid="export-modal-confirm">
                <Download className="w-3.5 h-3.5" /> {exporting ? "Esportazione..." : "Download Excel"}
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}


      <div className="luxury-card p-4 mb-6 flex items-center gap-3">
        <Search className="w-4 h-4 text-[#707070]" strokeWidth={1.5} />
        <input data-testid="search-input" placeholder="Cerca per nome, codice..."
               className="product-ux-placeholder flex-1 min-w-0 outline-none bg-transparent text-sm"
               value={filters.q} onChange={(e)=>setFilters({...filters, q: e.target.value})}
               onKeyDown={(e)=>e.key === "Enter" && fetchAll()} />
        <button onClick={fetchAll} className="text-xs tracking-luxury uppercase text-[#1A1A1A] hover:text-[#707070]" data-testid="search-submit-btn">Cerca</button>
      </div>

      {filterChips.length > 0 && (
        <div className="flex items-center flex-wrap gap-2 mb-6 -mt-2" data-testid="active-filters-bar">
          <span className="text-[10px] tracking-luxury uppercase text-[#707070] mr-1">Filtri attivi:</span>
          {filterChips.map(c => (
            <button key={c.key} onClick={() => removeFilter(c.key)}
                    className="inline-flex items-center gap-1.5 text-xs pl-3 pr-2 py-1 bg-[#F4F4F0] border border-[#E5E5E0] rounded-full hover:border-[#C5B39B] transition"
                    data-testid={`active-filter-${c.key}`}>
              {c.label}
              <X className="w-3 h-3 text-[#707070]" strokeWidth={2} />
            </button>
          ))}
          <button onClick={clearAllFilters}
                  className="text-[10px] tracking-luxury uppercase text-[#8C3A3A] hover:opacity-70 ml-1"
                  data-testid="active-filters-clear-all">Pulisci tutto</button>
        </div>
      )}

      {showFilters && (
        <div className="luxury-card p-6 mb-6 relative" data-testid="filters-panel">
          <button onClick={()=>setShowFilters(false)}
                  className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-sm text-[#707070] hover:text-[#1A1A1A] hover:bg-[#F4F4F0] transition text-xl leading-none"
                  aria-label="Chiudi filtri"
                  data-testid="filters-close-btn">×</button>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-5">
          <div>
            <label className="label-tiny">Brand</label>
            <select className={`input-luxury product-ux-filter-input ${!filters.brand_id ? "product-ux-select-empty" : ""}`} value={filters.brand_id}
                    data-testid="filter-brand"
                    onChange={(e)=>setFilters({...filters, brand_id: e.target.value})}>
              <option value="">Tutti</option>
              {brands.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </div>
          <div>
            <label className="label-tiny">Categoria</label>
            <select className={`input-luxury product-ux-filter-input ${!filters.category_id ? "product-ux-select-empty" : ""}`} value={filters.category_id}
                    data-testid="filter-category"
                    onChange={(e)=>setFilters({...filters, category_id: e.target.value, subcategory_id: ""})}>
              <option value="">Tutte</option>
              {topCats.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="label-tiny">Sotto-categoria</label>
            <select className={`input-luxury product-ux-filter-input ${!filters.subcategory_id ? "product-ux-select-empty" : ""}`} value={filters.subcategory_id}
                    data-testid="filter-subcategory"
                    disabled={!filters.category_id}
                    onChange={(e)=>setFilters({...filters, subcategory_id: e.target.value})}>
              <option value="">{filters.category_id ? "Tutte" : "Seleziona prima una categoria"}</option>
              {subcats.filter(s => !filters.category_id || s.parent_id === filters.category_id)
                       .map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
          <div>
            <label className="label-tiny">Codice prodotto</label>
            <input className="input-luxury product-ux-placeholder product-ux-filter-input" data-testid="filter-code" value={filters.code} placeholder="es. PRD-001"
                   onChange={(e)=>setFilters({...filters, code: e.target.value})} />
          </div>
          <div>
            <label className="label-tiny">Codice a barre</label>
            <input className="input-luxury product-ux-placeholder product-ux-filter-input" data-testid="filter-barcode" value={filters.barcode} placeholder="es. 8001234567890"
                   onChange={(e)=>setFilters({...filters, barcode: e.target.value})} />
          </div>
          <div>
            <label className="label-tiny">Ingrediente INCI</label>
            <input className="input-luxury product-ux-placeholder product-ux-filter-input" data-testid="filter-inci" value={filters.inci}
                   placeholder="es. HEMA"
                   onChange={(e)=>setFilters({...filters, inci: e.target.value})} />
          </div>
          <div>
            <label className="label-tiny">Codice CAS</label>
            <input className="input-luxury product-ux-placeholder product-ux-filter-input" data-testid="filter-cas" value={filters.cas}
                   placeholder="es. 868-77-9"
                   onChange={(e)=>setFilters({...filters, cas: e.target.value})} />
          </div>
          <div>
            <label className="label-tiny">% minima ingrediente</label>
            <input className="input-luxury product-ux-placeholder product-ux-filter-input" data-testid="filter-inci-percent" type="number" step="0.1" placeholder="es. 5"
                   value={filters.inci_min_percent}
                   onChange={(e)=>setFilters({...filters, inci_min_percent: e.target.value})} />
          </div>
          <div className="sm:col-span-2">
            <label className="label-tiny flex items-center justify-between">
              <span>Prezzo (€)</span>
              <span className="text-[#1A1A1A] font-medium tabular-nums">
                {filters.price_min !== "" ? filters.price_min : priceBounds.min}
                {" — "}
                {filters.price_max !== "" ? filters.price_max : priceBounds.max}
              </span>
            </label>
            <div className="pt-3 pb-1" data-testid="filter-price-slider-wrap">
              <Slider
                min={priceBounds.min}
                max={priceBounds.max}
                step={1}
                value={[
                  filters.price_min !== "" ? Number(filters.price_min) : priceBounds.min,
                  filters.price_max !== "" ? Number(filters.price_max) : priceBounds.max,
                ]}
                onValueChange={([mn, mx]) => setFilters({
                  ...filters,
                  price_min: mn > priceBounds.min ? mn : "",
                  price_max: mx < priceBounds.max ? mx : "",
                })}
                data-testid="filter-price-slider"
              />
            </div>
          </div>
          <div className="sm:col-span-2 lg:col-span-4 flex justify-end gap-3">
            <button onClick={clearAllFilters}
                    className="btn-ghost" data-testid="filters-clear-btn">Pulisci</button>
            <button onClick={()=>{ fetchAll(); setShowFilters(false); }} className="btn-primary" data-testid="filters-apply-btn">Applica</button>
          </div>
          </div>
        </div>
      )}

      {showCols && (
        <div className="luxury-card p-5 mb-6 flex flex-wrap gap-4" data-testid="columns-panel">
          {ALL_COLS.map(c => (
            <label key={c.key} className="flex items-center text-sm gap-2 cursor-pointer">
              <input type="checkbox" checked={cols.includes(c.key)} onChange={()=>toggleCol(c.key)}
                     data-testid={`col-toggle-${c.key}`} />
              {c.label}
            </label>
          ))}
        </div>
      )}

      <div className="luxury-card flex-1 min-h-[17rem] flex flex-col overflow-hidden">
        <div ref={scrollRef} className="overflow-auto flex-1 min-h-0">
        <table className="table-luxury sticky-thead" data-testid="products-table">
          <thead>
            <tr>
              {cols.includes("status") && <SortableTh field="status" label="Stato" sortField={sortField} sortDir={sortDir} onSort={toggleSort} />}
              {cols.includes("photo") && <th>Foto</th>}
              {cols.includes("code") && <SortableTh field="code" label="Codice" sortField={sortField} sortDir={sortDir} onSort={toggleSort} />}
              {cols.includes("name") && <SortableTh field="name" label="Nome" sortField={sortField} sortDir={sortDir} onSort={toggleSort} />}
              {cols.includes("brand") && <SortableTh field="brand" label="Brand" sortField={sortField} sortDir={sortDir} onSort={toggleSort} />}
              {cols.includes("category") && <SortableTh field="category" label="Categoria" sortField={sortField} sortDir={sortDir} onSort={toggleSort} />}
              {cols.includes("subcategory") && <SortableTh field="subcategory" label="Sottocategoria" sortField={sortField} sortDir={sortDir} onSort={toggleSort} />}
              {cols.includes("quantity") && <SortableTh field="quantity" label="Qtà" sortField={sortField} sortDir={sortDir} onSort={toggleSort} />}
              {cols.includes("price") && <SortableTh field="price" label="Prezzo" sortField={sortField} sortDir={sortDir} onSort={toggleSort} />}
              {cols.includes("qr") && <th>QR</th>}
              {cols.includes("updated") && <SortableTh field="updated" label="Modifica" sortField={sortField} sortDir={sortDir} onSort={toggleSort} />}
              {cols.includes("actions") && <th>Azioni</th>}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={cols.length} className="text-center text-[#707070] py-12">Caricamento...</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={cols.length} className="text-center text-[#707070] py-12">
                {onlyIncomplete ? "Nessun prodotto da completare. Ottimo lavoro!" : "Nessun prodotto. Inizia creandone uno nuovo."}
              </td></tr>
            ) : items.map(p => (
              <tr key={p.id} onClick={() => nav(`/products/${p.id}`)} className="cursor-pointer"
                   data-testid={`product-row-${p.code}`}>
                {cols.includes("status") && <td>
                  <CompletionBadge completion={p.completion} testId={`completion-${p.code}`} />
                </td>}
                {cols.includes("photo") && <td>
                  {p.primary_photo_url ? (
                    <img src={`${process.env.REACT_APP_BACKEND_URL}${p.primary_photo_url}`} alt=""
                         loading="lazy" decoding="async"
                         className="w-12 h-12 object-cover rounded-sm" />
                  ) : (
                    <div className="w-12 h-12 bg-[#F4F4F0] flex items-center justify-center rounded-sm">
                      <ImageIcon className="w-4 h-4 text-[#C5B39B]" strokeWidth={1.5} />
                    </div>
                  )}
                </td>}
                {cols.includes("code") && <td className="font-mono text-xs">{p.code}</td>}
                {cols.includes("name") && <td className="font-medium">{p.name}</td>}
                {cols.includes("brand") && <td>{brandMap[p.brand_id]?.name || "—"}</td>}
                {cols.includes("category") && <td>{catMap[p.category_id]?.name || "—"}</td>}
                {cols.includes("subcategory") && <td>{catMap[p.subcategory_id]?.name || "—"}</td>}
                {cols.includes("quantity") && <td>{p.quantity_value ? `${p.quantity_value} ${p.udm || ""}` : "—"}</td>}
                {cols.includes("price") && <td>{p.price_value ? `${p.price_value} ${p.price_currency || ""}` : "—"}</td>}
                {cols.includes("qr") && <td onClick={(e)=>e.stopPropagation()}>
                  {p.qr_png_file_id && !subscriptionDisabled ? (
                    <button onClick={()=>setQrModal(p)}
                            className="inline-flex items-center justify-center w-8 h-8 rounded-sm border border-transparent hover:border-[#E5E5E0] hover:bg-[#F4F4F0] transition text-[#1A1A1A]"
                            data-testid={`product-qr-btn-${p.code}`}
                            title="Anteprima QR">
                      <QrCode className="w-4 h-4" strokeWidth={1.5} />
                    </button>
                  ) : "—"}
                </td>}
                {cols.includes("updated") && <td className="text-xs text-[#707070]">{p.updated_at?.slice(0,10)}</td>}
                {cols.includes("actions") && <td onClick={(e)=>e.stopPropagation()}>
                  <div className="inline-flex items-center gap-1">
                    {isAdmin && (
                      <button onClick={()=>setInciModal(p)}
                              className="inline-flex items-center gap-1.5 px-2 py-1 text-xs text-[#707070] hover:text-[#1A1A1A] border border-transparent hover:border-[#E5E5E0] rounded-sm transition"
                              data-testid={`product-inci-analysis-btn-${p.code}`}
                              title="Analisi INCI">
                        <ShieldCheck className="w-3.5 h-3.5" strokeWidth={1.5}/>
                        <span className="hidden lg:inline tracking-luxury uppercase text-[10px]">INCI</span>
                      </button>
                    )}
                    {canWrite && <button onClick={()=>setDupModal(p)}
                            className="inline-flex items-center gap-1.5 px-2 py-1 text-xs text-[#707070] hover:text-[#1A1A1A] border border-transparent hover:border-[#E5E5E0] rounded-sm transition"
                            data-testid={`product-duplicate-btn-${p.code}`}
                            title="Duplica prodotto">
                      <Copy className="w-3.5 h-3.5" strokeWidth={1.5}/>
                      <span className="hidden lg:inline tracking-luxury uppercase text-[10px]">Duplica</span>
                    </button>}
                  </div>
                </td>}
              </tr>
            ))}
            {/* Infinite-scroll sentinel: always present so the observer can
                attach; shows the loader while appending and an end marker. */}
            <tr ref={sentinelRef} aria-hidden="true" data-testid="infinite-scroll-sentinel">
              <td colSpan={cols.length} className="text-center py-4 text-xs text-[#707070]">
                {loadingMore ? "Caricamento altri prodotti..."
                  : (!loading && items.length > 0 && !hasMore) ? "— Fine dell'elenco —" : ""}
              </td>
            </tr>
          </tbody>
        </table>
        </div>
      </div>
      {qrModal && <ProductQrModal product={qrModal} onClose={()=>setQrModal(null)} onRegen={fetchAll}/>}
      {dupModal && <DuplicateProductModal product={dupModal}
                                              onClose={()=>setDupModal(null)}
                                              onDone={(newP)=>{ setDupModal(null); nav(`/products/${newP.id}`); }}/>}
      {inciModal && <InciAnalysisModal product={inciModal} onClose={()=>setInciModal(null)} />}
    </div>
  );
}


function InciAnalysisModal({ product, onClose }) {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);

  useEffect(() => {
    let cancel = false;
    api.get(`/products/${product.id}/inci/analysis`).then(r => {
      if (cancel) return;
      setAnalysis(r.data?.analysis || null);
      setUpdatedAt(r.data?.updated_at || null);
    }).catch(() => {}).finally(() => !cancel && setLoading(false));
    return () => { cancel = true; };
  }, [product.id]);

  const run = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/products/${product.id}/inci/analyze`);
      setAnalysis(r.data?.analysis || null);
      setUpdatedAt(new Date().toISOString());
      toast.success("Analisi INCI aggiornata");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore analisi");
    } finally { setBusy(false); }
  };

  return (
    <div className="products-ux fixed inset-0 bg-black/40 z-50 flex items-center justify-center px-4"
         onClick={onClose} data-testid="dashboard-inci-modal">
      <div className="bg-white rounded-sm shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto p-6"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="font-serif text-2xl">Analisi INCI</h3>
            <div className="text-xs text-[#707070] mt-1">{product.code} · {product.name}</div>
          </div>
          <button onClick={onClose} className="text-[#707070] hover:text-[#1A1A1A]"
                  data-testid="dashboard-inci-modal-close"><X className="w-4 h-4"/></button>
        </div>
        {loading ? (
          <div className="py-8 text-center text-sm text-[#707070]">Caricamento...</div>
        ) : (
          <InciAnalysisPanel analysis={analysis} updatedAt={updatedAt} onRefresh={run} busy={busy} />
        )}
      </div>
    </div>
  );
}


function SortableTh({ field, label, sortField, sortDir, onSort }) {
  const active = sortField === field;
  const Icon = !active ? ChevronsUpDown : (sortDir === "asc" ? ArrowUp : ArrowDown);
  return (
    <th onClick={() => onSort(field)}
        className={`cursor-pointer select-none group ${active ? "text-[#1A1A1A]" : ""}`}
        data-testid={`th-sort-${field}`}
        title={`Ordina per ${label}${active ? (sortDir === "asc" ? " (crescente)" : " (decrescente)") : ""}`}>
      <span className="inline-flex items-center gap-1.5">
        <span>{label}</span>
        <Icon className={`w-3 h-3 transition ${active ? "text-[#1A1A1A]" : "text-[#C5B39B] opacity-70 group-hover:opacity-100"}`} strokeWidth={2}/>
      </span>
    </th>
  );
}

function DuplicateProductModal({ product, onClose, onDone }) {
  const [newCode, setNewCode] = useState(`${product.code}_COPY`);
  const [newName, setNewName] = useState(product.name);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!newCode.trim()) {
      toast.error("Inserisci un codice");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post(`/products/${product.id}/duplicate`, {
        new_code: newCode.trim(),
        new_name: newName.trim() || product.name,
      });
      toast.success("Prodotto duplicato");
      onDone && onDone(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore durante la duplicazione");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-6"
         onClick={onClose} data-testid="duplicate-product-modal">
      <div className="bg-white max-w-md w-full p-8 luxury-card" onClick={e=>e.stopPropagation()}>
        <div className="mb-6">
          <div className="text-[10px] tracking-luxury text-[#707070] uppercase mb-1">Duplica prodotto</div>
          <h3 className="font-serif text-2xl">{product.code}</h3>
          <div className="text-sm text-[#707070] mt-1">{product.name}</div>
        </div>
        <p className="text-xs text-[#707070] mb-5 leading-relaxed">
          Verranno copiati i dati del prodotto (descrizioni, avvertenze, uso,
          caratteristiche, quantità, prezzo, pittogrammi, provenienza).
          <br/>
          <span className="text-[#8A8579]">Non vengono copiati:</span> fornitore
          (anche packaging), video YouTube, link sito, foto, etichette,
          scheda di sicurezza, INCI e barcode. Il QR viene rigenerato.
        </p>
        <div className="space-y-4 mb-6">
          <div>
            <label className="label-tiny">Nuovo codice*</label>
            <input className="input-luxury" value={newCode} autoFocus
                   onChange={(e)=>setNewCode(e.target.value)}
                   data-testid="duplicate-new-code" />
          </div>
          <div>
            <label className="label-tiny">Nuovo nome (facoltativo)</label>
            <input className="input-luxury" value={newName}
                   onChange={(e)=>setNewName(e.target.value)}
                   data-testid="duplicate-new-name" />
          </div>
        </div>
        <div className="flex gap-3 justify-end">
          <button onClick={onClose} className="btn-ghost" data-testid="duplicate-cancel-btn">
            Annulla
          </button>
          <button onClick={submit} disabled={busy} className="btn-primary disabled:opacity-50"
                  data-testid="duplicate-confirm-btn">
            {busy ? "Duplicazione..." : "Duplica"}
          </button>
        </div>
      </div>
    </div>
  );
}


function ProductQrModal({ product, onClose, onRegen }) {
  const [p, setP] = useState(product);
  const [busy, setBusy] = useState(false);
  const apiBase = process.env.REACT_APP_BACKEND_URL;
  const publicUrl = `${window.location.origin}/p/${p.public_slug}`;

  const dl = async (fmt) => {
    const token = getActiveToken();
    const res = await fetch(
      `${apiBase}/api/products/${p.id}/qr/download?format=${fmt}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    const blob = await res.blob();
    const cd = res.headers.get("content-disposition") || "";
    const m = /filename="?([^"]+)"?/i.exec(cd);
    const fname = m ? m[1] : `qr.${fmt}`;
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = fname;
    a.click();
    URL.revokeObjectURL(a.href);
  };
  const regen = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/products/${p.id}/qr/regenerate`);
      setP(prev => ({ ...prev,
        qr_png_file_id: r.data.qr_png_file_id,
        qr_svg_file_id: r.data.qr_svg_file_id,
        qr_pdf_file_id: r.data.qr_pdf_file_id,
      }));
      toast.success("QR rigenerato");
      onRegen && onRegen();
    } catch { toast.error("Errore rigenerazione QR"); }
    finally { setBusy(false); }
  };
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      toast.success("Link copiato");
    } catch {
      toast.error("Impossibile copiare il link");
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-6"
         onClick={onClose} data-testid="product-qr-modal">
      <div className="bg-white max-w-md w-full p-8 luxury-card" onClick={e=>e.stopPropagation()}>
        <div className="flex justify-between items-start mb-6">
          <div>
            <div className="text-[10px] tracking-luxury text-[#707070] uppercase mb-1">QR Prodotto</div>
            <h3 className="font-serif text-2xl">{p.code}</h3>
            <div className="text-sm text-[#707070] mt-1">{p.name}</div>
          </div>
          <button onClick={onClose} className="text-[#707070] hover:text-[#1A1A1A] text-2xl leading-none"
                  data-testid="product-qr-close">×</button>
        </div>

        <div className="flex flex-col items-center gap-4 mb-6">
          {p.qr_png_file_id ? (
            <img src={`${apiBase}/api/files/${p.qr_png_file_id}`}
                 className="w-56 h-56 border border-[#E5E5E0]"
                 alt="QR prodotto" data-testid="product-qr-image"/>
          ) : (
            <div className="w-56 h-56 border border-dashed border-[#C5B39B] flex items-center justify-center text-xs text-[#707070]">
              QR non ancora generato
            </div>
          )}
          <code className="text-xs bg-[#F4F4F0] px-3 py-2 rounded-sm break-all text-center"
                data-testid="product-qr-url">{publicUrl}</code>
        </div>

        <div className="grid grid-cols-3 gap-2 mb-3">
          <button onClick={()=>dl("png")} className="btn-ghost flex items-center justify-center"
                  data-testid="product-qr-download-png">
            <Download className="w-3 h-3 mr-2"/>PNG
          </button>
          <button onClick={()=>dl("svg")} className="btn-ghost flex items-center justify-center"
                  data-testid="product-qr-download-svg">
            <Download className="w-3 h-3 mr-2"/>SVG
          </button>
          <button onClick={()=>dl("pdf")} className="btn-ghost flex items-center justify-center"
                  data-testid="product-qr-download-pdf">
            <Download className="w-3 h-3 mr-2"/>PDF
          </button>
        </div>
        <div className="flex gap-2">
          <button onClick={copy} className="btn-ghost flex-1" data-testid="product-qr-copy-url">
            Copia link
          </button>
          <button onClick={regen} disabled={busy}
                  className="btn-ghost flex-1 disabled:opacity-50"
                  data-testid="product-qr-regen">
            {busy ? "Rigenerazione..." : "Rigenera"}
          </button>
        </div>
      </div>
    </div>
  );
}


/**
 * Non-intrusive completion indicator.
 *
 * - Fully complete (no missing field) → small green check icon.
 * - Incomplete → soft amber dot + count of missing fields.
 *
 * Hovering the badge reveals a popover listing every missing field so the
 * admin doesn't have to open the product to find out what is left to do.
 * The popover stops row clicks (e.stopPropagation) so opening it does NOT
 * navigate to the product page.
 */
function CompletionBadge({ completion, testId }) {
  const missing = completion?.missing || [];
  const isComplete = missing.length === 0;
  const percent = completion?.percent ?? 0;
  const stop = (e) => e.stopPropagation();
  const anchorRef = useRef(null);
  const hideTimer = useRef(null);
  const [pop, setPop] = useState(null); // { left, top, below }

  const POP_W = 256; // w-64

  const show = () => {
    if (hideTimer.current) { clearTimeout(hideTimer.current); hideTimer.current = null; }
    const el = anchorRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const estH = 56 + missing.length * 22; // header + rows
    const spaceAbove = Math.max(0, r.top - 14);
    const spaceBelow = Math.max(0, window.innerHeight - r.bottom - 14);
    // Prefer the side that fits; otherwise use the side with more room and scroll.
    const below = spaceBelow >= estH || (spaceAbove < estH && spaceBelow >= spaceAbove);
    let left = Math.min(r.left, window.innerWidth - POP_W - 8);
    left = Math.max(8, left);
    const top = below ? r.bottom + 6 : r.top - 6;
    const maxHeight = Math.floor(below ? spaceBelow : spaceAbove);
    setPop({ left, top, below, maxHeight });
  };
  const scheduleHide = () => {
    if (hideTimer.current) clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => setPop(null), 120);
  };

  useEffect(() => () => { if (hideTimer.current) clearTimeout(hideTimer.current); }, []);

  if (isComplete) {
    return (
      <div data-testid={testId} title="Tutti i campi compilati">
        <ProductStatus tone="success" icon={CheckCircle2} testId={`${testId}-badge`}>Completo</ProductStatus>
      </div>
    );
  }
  return (
    <div className="relative inline-block" onClick={stop} data-testid={testId}
         ref={anchorRef} onMouseEnter={show} onMouseLeave={scheduleHide}>
      <ProductStatus as="button" type="button" tone="warning" icon={AlertCircle}
           onClick={(e) => { stop(e); show(); }} onFocus={show} onBlur={scheduleHide}
           onKeyDown={(e) => { if (e.key === "Escape") { e.stopPropagation(); setPop(null); } }}
           aria-expanded={!!pop} aria-controls={pop ? `${testId}-details` : undefined}
           aria-label={`${missing.length} ${missing.length === 1 ? "campo" : "campi"} da completare. Mostra i dettagli`}
           testId={`${testId}-badge`}>
          {missing.length} {missing.length === 1 ? "campo" : "campi"}
      </ProductStatus>
      {/* Rendered in a portal with fixed positioning so it is NEVER clipped by
          the table's overflow container; flips above/below depending on the
          available space (first rows open downward, the rest upward). */}
      {pop && createPortal(
        <div
          style={{
            position: "fixed",
            left: pop.left,
            top: pop.top,
            transform: pop.below ? "none" : "translateY(-100%)",
            maxHeight: pop.maxHeight,
          }}
          id={`${testId}-details`} role="region" aria-label="Campi da completare"
          className="products-ux z-[80] w-64 bg-white border border-[#E5E5E0] shadow-lg rounded-sm p-3 overflow-y-auto"
          onMouseEnter={show}
          onMouseLeave={scheduleHide}
          onClick={stop}
          data-testid={`${testId}-popover`}>
          <div className="text-[10px] tracking-luxury uppercase text-[#707070] mb-2 flex items-center justify-between">
            <span>Da completare</span>
            <span className="product-ux-warning-text font-medium" data-testid={`${testId}-percent`}>{percent}%</span>
          </div>
          <ul className="space-y-1">
            {missing.map(m => (
              <li key={m} className="text-xs text-[#1A1A1A] flex items-start gap-2"
                  data-testid={`${testId}-missing-${m.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <span className="product-ux-warning-dot inline-block w-1 h-1 rounded-full mt-[6px] shrink-0"/>
                <span>{m}</span>
              </li>
            ))}
          </ul>
        </div>,
        document.body,
      )}
    </div>
  );
}
